/**
 * Operator-preview copy of the player's `videoOverlays` contract (21m-event-player
 * `sequence-video-overlay.helpers.ts`): embedded presentation videos over their posters.
 */

export type PresentationVideoOverlay = {
  id: string;
  url: string;
  /** Placement normalized to the slide (0..1). */
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  crop?: { left: number; top: number; right: number; bottom: number };
  /** 1-based frames of the video's slide. */
  fromFrame: number;
  toFrame: number;
  startFrame?: number;
  startDelayMs: number;
  trimStartMs: number;
  trimFromEndMs: number;
  /** File length. The preview seeks and stops from this when the stage already has a start stamp. */
  durationMs?: number;
  /** Hide the video whenever it is not playing. */
  hideWhenStopped?: boolean;
};

export type PresentationVideoOverlays = { aspectRatio: number; videos: PresentationVideoOverlay[] };

function finiteOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function parseCrop(value: unknown): PresentationVideoOverlay['crop'] {
  if (!value || typeof value !== 'object') return undefined;
  const crop = value as Record<string, unknown>;
  const left = finiteOr(crop.left, 0);
  const top = finiteOr(crop.top, 0);
  const right = finiteOr(crop.right, 0);
  const bottom = finiteOr(crop.bottom, 0);
  if (left + right >= 1 || top + bottom >= 1 || (!left && !top && !right && !bottom)) return undefined;
  return { left, top, right, bottom };
}

function parseVideo(value: unknown): PresentationVideoOverlay | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const video = value as Record<string, unknown>;
  const fromFrame = finiteOr(video.fromFrame, 0);
  const toFrame = finiteOr(video.toFrame, 0);
  const width = finiteOr(video.width, 0);
  const height = finiteOr(video.height, 0);
  if (
    typeof video.url !== 'string' ||
    !video.url ||
    fromFrame < 1 ||
    toFrame < fromFrame ||
    width <= 0 ||
    height <= 0
  ) {
    return undefined;
  }
  const startFrame = finiteOr(video.startFrame, 0);
  const crop = parseCrop(video.crop);
  return {
    id: typeof video.id === 'string' ? video.id : video.url,
    url: video.url,
    x: finiteOr(video.x, 0),
    y: finiteOr(video.y, 0),
    width,
    height,
    rotation: finiteOr(video.rotation, 0),
    ...(crop ? { crop } : {}),
    fromFrame,
    toFrame,
    ...(startFrame >= fromFrame && startFrame <= toFrame ? { startFrame } : {}),
    startDelayMs: Math.max(0, finiteOr(video.startDelayMs, 0)),
    trimStartMs: Math.max(0, finiteOr(video.trimStartMs, 0)),
    trimFromEndMs: Math.max(0, finiteOr(video.trimFromEndMs, 0)),
    ...(finiteOr(video.durationMs, 0) > 0 ? { durationMs: finiteOr(video.durationMs, 0) } : {}),
    ...(video.hideWhenStopped === true ? { hideWhenStopped: true } : {}),
  };
}

/** A deck can play sound when it carries at least one embedded video. */
export function presentationPreviewHasVideoAudio(raw: string | undefined): boolean {
  return parsePresentationVideoOverlays(raw) != null;
}

export function parsePresentationVideoOverlays(raw: string | undefined): PresentationVideoOverlays | undefined {
  if (!raw) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (!parsed || typeof parsed !== 'object') return undefined;
  const { aspectRatio, videos } = parsed as Record<string, unknown>;
  const ratio = finiteOr(aspectRatio, 0);
  if (ratio <= 0 || !Array.isArray(videos)) return undefined;
  const list = videos.map(parseVideo).filter((video): video is PresentationVideoOverlay => !!video);
  return list.length ? { aspectRatio: ratio, videos: list } : undefined;
}

export function presentationVideosOnFrame(
  overlays: PresentationVideoOverlays | undefined,
  frameIndex: number,
): PresentationVideoOverlay[] {
  return overlays?.videos.filter((video) => frameIndex >= video.fromFrame && frameIndex <= video.toFrame) ?? [];
}

/** Same PowerPoint start rules as the player; `previousFrame` is undefined on entering the slide. */
export function resolvePresentationVideoTransition(
  startFrame: number | undefined,
  previousFrame: number | undefined,
  frameIndex: number,
): 'reset' | 'startDelayed' | 'startNow' | 'keep' {
  if (startFrame == null || frameIndex < startFrame) return 'reset';
  if (previousFrame != null && previousFrame >= startFrame) return 'keep';
  return frameIndex === startFrame ? 'startDelayed' : 'startNow';
}

/** Inner video box (percent of the overlay box) that hides the cropped edges. */
export function presentationVideoCropBox(crop: PresentationVideoOverlay['crop']): {
  left: string;
  top: string;
  width: string;
  height: string;
} {
  if (!crop) return { left: '0%', top: '0%', width: '100%', height: '100%' };
  const visibleWidth = 1 - crop.left - crop.right;
  const visibleHeight = 1 - crop.top - crop.bottom;
  const percent = (value: number) => `${Number((value * 100).toFixed(4))}%`;
  return {
    left: percent(-crop.left / visibleWidth),
    top: percent(-crop.top / visibleHeight),
    width: percent(1 / visibleWidth),
    height: percent(1 / visibleHeight),
  };
}
